import { Delete } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { observer } from 'mobx-react';
import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { useI18n } from '../../../../../../application/language/useI18n';
import { PasswordField } from '../../../design/form/PasswordField';
import { ContentImportDomain } from './store/ContentImportDomain';
import { IContentImportDomain } from './store/IContentImportDomain';

export const ContentImportPage = observer(() => {
  const [importDomain] = useState<IContentImportDomain>(new ContentImportDomain());
  const { translate } = useI18n();

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      importDomain.handleFilesDrop(acceptedFiles);
    },
    [importDomain],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/zip': ['.zip'] },
    maxSize: 50 * 1024 * 1024,
    onDropRejected: (fileRejections) => {
      const errors = fileRejections.map((rej) => {
        if (rej.errors.some((e) => e.code === 'file-too-large')) {
          return translate('contentTransfer.importPage.fileTooLarge', { name: rej.file.name });
        }
        return translate('contentTransfer.importPage.fileNotSupported', { name: rej.file.name });
      });
      importDomain.ui.dropzoneErrors.setList(errors);
    },
  });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '93vh',
        p: 2,
        boxSizing: 'border-box',
      }}
    >
      <Paper
        {...getRootProps()}
        sx={{
          p: 4,
          textAlign: 'center',
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.400',
          borderRadius: 2,
          cursor: 'pointer',
          mb: 2,
        }}
      >
        <input {...getInputProps()} />
        <Typography>
          {isDragActive
            ? translate('contentTransfer.importPage.dropZoneDescriptionShort')
            : translate('contentTransfer.importPage.dropZoneDescription')}
        </Typography>
      </Paper>

      {importDomain.ui.dropzoneErrors.list.length > 0 && (
        <Box sx={{ color: 'error.main', mt: 1 }}>
          {importDomain.ui.dropzoneErrors.list.map((err, idx) => (
            <Typography key={idx} variant="body2">
              • {err}
            </Typography>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Tooltip title={translate('contentTransfer.importPage.tooltip')} placement="top-start">
          <FormControlLabel
            control={
              <Checkbox
                checked={importDomain.ui.withDependencies.entity}
                onChange={(e) => importDomain.ui.withDependencies.setEntity(e.target.checked)}
              />
            }
            label={translate('contentTransfer.importPage.toggle')}
          />
        </Tooltip>

        <Button
          variant="outlined"
          size="small"
          disabled={importDomain.ui.unitsForImport.list.length === 0}
          onClick={() => importDomain.clearAllFiles()}
        >
          {translate('contentTransfer.importPage.clearAll')}
        </Button>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <List>
          {importDomain.ui.unitsForImport.list.map((file) => (
            <ListItem
              key={file.file.name}
              secondaryAction={
                <IconButton edge="end" color="error" onClick={() => importDomain.removeFile(file.file.name)}>
                  <Delete />
                </IconButton>
              }
            >
              <ListItemText
                primary={file.file.name}
                secondary={
                  file.status === 'success'
                    ? translate('contentTransfer.importPage.unitState')
                    : file.status === 'error'
                    ? translate('contentTransfer.importPage.importError', { message: file.message ?? '' })
                    : translate('contentTransfer.importPage.pending')
                }
                secondaryTypographyProps={{
                  color:
                    file.status === 'error'
                      ? 'error.main'
                      : file.status === 'success'
                      ? 'success.main'
                      : 'text.secondary',
                }}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          disabled={importDomain.ui.unitsForImport.list.length === 0 || importDomain.ui.isImporting.entity}
          onClick={() => importDomain.openPasswordDialog()}
        >
          {importDomain.ui.isImporting.entity
            ? `${translate('contentTransfer.importPage.state')} ${importDomain.ui.importProgress.entity.current}/${
                importDomain.ui.importProgress.entity.total
              }`
            : translate('contentTransfer.importPage.button')}
        </Button>
      </Box>

      {importDomain.ui.importResults.list.length > 0 && (
        <Box sx={{ mt: 2 }}>
          {importDomain.ui.importResults.list.map((res, idx) => (
            <Typography key={idx} variant="body2" color={res.success ? 'success.main' : 'error.main'}>
              {res.filename}: {res.message}
            </Typography>
          ))}
        </Box>
      )}

      <Dialog
        fullWidth
        open={importDomain.ui.isPasswordDialogOpen.entity}
        onClose={() => importDomain.closePasswordDialog()}
      >
        <DialogTitle>{translate('contentTransfer.importPage.modalTitle')}</DialogTitle>
        <DialogContent>
          <PasswordField
            label={translate('contentTransfer.importPage.inputPlaceholder')}
            value={importDomain.ui.importPassword.entity}
            errorMessage={importDomain.ui.passwordError.entity}
            onChange={(val) => {
              importDomain.ui.importPassword.setEntity(val);
              if (importDomain.ui.passwordError.entity) {
                importDomain.ui.passwordError.setEntity(null);
              }
            }}
            autoFocus
            onEnter={importDomain.checkPasswordAndImport}
            name="import-secret"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => importDomain.closePasswordDialog()}>
            {translate('contentTransfer.importPage.decline')}
          </Button>
          <Button
            variant="contained"
            onClick={() => importDomain.checkPasswordAndImport()}
            disabled={!importDomain.ui.importPassword.entity}
          >
            {translate('contentTransfer.importPage.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});
